import { componentCls } from "@faire/slate/lib/tracking";
import { AvatarBase } from "@faire/slate/private/avatar/AvatarBase";
import {
  AvatarIcon,
  IAvatarIconProps as IIconProps,
} from "@faire/slate/private/avatar/AvatarIcon";
import { getAvatarImageUrl } from "@faire/slate/private/avatar/getAvatarImageUrl";
import { AvatarSize } from "@faire/slate/private/avatar/types";
import { getAvatarIconSize } from "@faire/slate/private/avatar/utils";
import { IImage } from "@faire/types-common-api/indigofair/data/IImage";
import React from "react";

interface ISlateProps {
  /**
   * The border color of the avatar
   */
  borderColor?: string;

  /**
   * The label to display when there is no image.
   *
   * Only the first letter of the label will be displayed.
   *
   * When an image is provided without an alt text, the label will be used as the alt text.
   */
  label?: string;

  /**
   * The icon props to display
   *
   *  ```ts
   *  interface IIconProps {
   *    // The icon component to render
   *    Component: React.FC<SVGIconProps>;
   *    // The position of the icon
   *    // "topRight" (default) | "bottomRight"
   *    position?: AvatarIconPosition;
   *    // Flag to show the icon container.
   *    showContainer?: boolean;
   *  }
   *  ```
   */
  iconProps?: IIconProps;

  /**
   * The `IImage` to display
   */
  image?: Partial<IImage>;

  /**
   * The size of the avatar
   *
   * xxSmall=32, xSmall=40, small=48, medium=56, large=64, xLarge=96
   *
   * @default medium
   */
  size?: AvatarSize;

  // DO NOT ADD any new prop without aligning with Slate team first
}

/**
 * Avatars are used to show a thumbnail representation of an individual or business.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Avatar-2242efb5c25a8000a180ce5c692f310f | Slate/Avatar}
 */
export const Avatar: React.FC<ISlateProps> = ({
  borderColor,
  iconProps,
  image,
  label,
  size = "medium",
}) => {
  const imageUrl = getAvatarImageUrl(image ? IImage.build(image) : undefined);
  const iconSize = getAvatarIconSize(size);

  return (
    <AvatarBase
      borderColor={borderColor}
      className={componentCls()}
      data-component-name="Slate/Avatar"
      imageUrl={imageUrl}
      label={image?.alt_text ?? label ?? ""}
      size={size}
    >
      {iconProps ? <AvatarIcon {...iconProps} size={iconSize} /> : null}
    </AvatarBase>
  );
};
